import * as React from 'react';
import { LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TenantPicker, type TenantOption } from './tenant-picker';

/**
 * The `account` node of `AppShell`'s identity strip (§5.5 of the component
 * inventory: the account menu).
 *
 * It composes `TenantPicker` in its `menu` form — the one that contract describes
 * as the shell's switcher, and which until now had no consumer: screen 3 uses the
 * `list` form — and adds the session's email to it.
 *
 * It fetches nothing and dispatches nothing: it receives the already-resolved
 * account and reports through `onTenantChange` and `onLogout`, just like
 * `TenantPicker` (§3, convention 4). Whoever mounts it — `AppLayout` — is what
 * decides what a tenant change does.
 *
 * With more than 7 organisations the switcher's popup carries a search field
 * (see `TenantPicker`); this menu passes every organisation through unfiltered.
 */
export interface AccountMenuProps {
  email: string;
  tenants: readonly TenantOption[];
  activeTenantId: string | null;
  onTenantChange: (tenantId: string) => void;
  onLogout: () => void;
  /** Disables the button while the `POST /auth/logout` is in flight. */
  loggingOut?: boolean;
}

export function AccountMenu({
  email,
  tenants,
  activeTenantId,
  onTenantChange,
  onLogout,
  loggingOut = false,
}: AccountMenuProps): React.JSX.Element {
  const logoutLabel = loggingOut ? 'Cerrando sesión…' : 'Cerrar sesión';
  return (
    <div className="flex min-w-0 items-center gap-inset-sm sm:gap-inset">
      {/* A single organisation is not a choice: the switcher hides and only the
       * email is left. A one-item menu invites opening it to discover there is
       * nothing to choose. */}
      {tenants.length > 1 ? (
        <TenantPicker
          layout="menu"
          tenants={tenants}
          value={activeTenantId}
          onChange={onTenantChange}
        />
      ) : null}
      {/* `truncate` with `max-w`: a long corporate email must not push the
       * environment badge out of the strip — it is the element contract §5.5
       * requires to be visible at all times.
       *
       * And on mobile it is not shown at all. At 390px the strip has to carry
       * the environment, the active company and the way out of the session, and
       * of those four the email is the only one that governs no decision: which
       * environment I am in and which company I am using do change what happens
       * on issuing, and logging out is the way off a shared computer. The email
       * stays one tap away, on the account screen. */}
      <span
        className="hidden max-w-48 truncate text-body-s text-fg-muted sm:block"
        title={email}
      >
        {email}
      </span>
      {/* Visible, not hidden behind a dropdown. Logging out is the way off a
       * shared computer: if it has to be discovered, the alternative people
       * choose is closing the tab, which closes nothing.
       *
       * The icon accompanies the label, never replaces it: an icon alone forces
       * guessing, and the nav drawer (below `sm`, where this block lives on a
       * phone) has room for both. There the target grows to the 44px touch
       * floor.
       *
       * Visible, but not loud: `fg-muted` at rest, full foreground on hover.
       * In full `fg` and semibold it was the heaviest thing on the strip's
       * right side — heavier than the active company — and the way out of the
       * session is something to find, not something to be drawn to. */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="shrink-0 text-fg-muted hover:text-fg max-sm:min-h-11"
        onClick={onLogout}
        disabled={loggingOut}
      >
        <LogOut aria-hidden="true" className="size-icon-sm" />
        {logoutLabel}
      </Button>
    </div>
  );
}
